import { useState } from 'react';
import { MaternalLabs, LabTrends } from '@/features/labs';
import { PregnancyImaging } from '@/features/imaging';
import { usePermissions } from '@/features/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePregnancies } from '../api/pregnancies-api';
import type { PatientMetadata } from '../api/patients-api';
import { UploadedDocumentInboxSheet } from './UploadedDocumentInboxSheet';

/** Read the same approved results used by the pregnancy chart, without copying them. */
export function PatientResults({ patientId, metadata }: { patientId: string; metadata?: PatientMetadata | null }) {
  const { can } = usePermissions();
  const pregnancies = usePregnancies(patientId);
  const [source, setSource] = useState<NonNullable<PatientMetadata['referral_history']>[number]>();
  const imaging = metadata?.referral_history?.filter((entry) => entry.field === 'imaging_reports') || [];
  return <>
    <Card>
      <CardHeader><CardTitle>Labs</CardTitle></CardHeader>
      <CardContent>
        <p className="mb-4">Approved results from this patient’s documents and pregnancy charts. New extracted results appear here after review.</p>
        <Tabs defaultValue="table">
          <TabsList><TabsTrigger value="table">Flowsheet</TabsTrigger><TabsTrigger value="trends">Value trends</TabsTrigger></TabsList>
          <TabsContent value="table"><MaternalLabs patientId={patientId} /></TabsContent>
          <TabsContent value="trends"><LabTrends patientId={patientId} /></TabsContent>
        </Tabs>
      </CardContent>
    </Card>
    <Card>
      <CardHeader><CardTitle>Imaging</CardTitle></CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p>Reviewed findings from incoming records. Open the source for the study date, full report and supporting evidence.</p>
        {imaging.length ? <Table>
          <TableHeader><TableRow><TableHead>Finding / report</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
          <TableBody>{imaging.map((entry, index) => <TableRow key={`${entry.document_id}-${index}`}>
            <TableCell className="whitespace-pre-wrap">{entry.value}{entry.conflict && <p>Differs from another chart statement; both retained for review.</p>}</TableCell>
            <TableCell>{can('read_document') && <Button variant="outline" onClick={() => setSource(entry)}>Open source{entry.page ? ` · page ${entry.page}` : ''}</Button>}</TableCell>
          </TableRow>)}</TableBody>
        </Table> : <p>No reviewed imaging reports imported yet.</p>}
        {pregnancies.isError && <p role="alert">Pregnancy imaging could not be loaded.</p>}
        {pregnancies.data?.data.map((pregnancy) => <details key={pregnancy.id}>
          <summary>Pregnancy imaging · due {pregnancy.edd || 'date not recorded'}</summary>
          <PregnancyImaging pregnancyId={pregnancy.id} initialTab="fetal" />
        </details>)}
      </CardContent>
    </Card>
    <UploadedDocumentInboxSheet open={!!source} documentId={source?.document_id} revisionId={source?.revision_id} pageNumber={source?.page} onOpenChange={(open) => { if (!open) setSource(undefined); }} />
  </>;
}
